import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@hooks';
import { Stack, Typography } from '@esgian/esgianui';
import { UTC_TIMESTAMP } from '@constants/index';
import CanvasTimeSeriesChart from '@components/Charts/CanvasTimeSeriesChart/CanvasTimeSeriesChart';
import moment from 'moment-timezone';
import { NoDataAvailable } from '@components/Display';

let tooltipText = (
  <Stack spacing={2}>
    <Typography variant={'caption'}>
      Total regenerated energy from the drilling VFD&apos;s burned or reused.
    </Typography>
    <Typography variant={'caption'}>
      The reused energy is consumed by the other drives on the VFD switchboard and is considered as
      free energy.
    </Typography>
    <Typography variant={'caption'}>
      The burned energy is wasted energy because it is burned over the resistor banks via the brake
      choppers on the switchboard.
    </Typography>
    <Typography variant={'caption'}>
      For context, clicking on one of the bars will reveal the details per hour for the day before,
      selected day and the next day for the Equipment in the Power details graph below.
    </Typography>
  </Stack>
);

function DailyRegeneratedEnergyDrawworks({
  chartRef,
  syncRefs,
  chartData,
  loading,
  highlightPeriod
}) {
  const {
    theme: { palette }
  } = useTheme();

  const { unit, series } = useMemo(() => {
    if (!chartData || !chartData.reused || !chartData.burned) return {};
    return {
      series: [
        {
          type: 'bar',
          stack: 1,
          borderSkipped: true,
          backgroundColor: palette.success.main,
          label: 'Reused',
          data: chartData.reused
        },
        {
          type: 'bar',
          stack: 1,
          borderSkipped: true,
          backgroundColor: palette.error.main,
          label: 'Burned',
          legendTooltip: tooltipText,
          data: chartData.burned
        }
      ],
      unit: chartData.unit
    };
  }, [chartData]);

  const handleColumnClick = useCallback(
    (val) => {
      const { index } = val;
      const timestamp = series[0]?.data[index].x;

      chartData.setSelectedColumn({
        date: moment.utc(parseInt(timestamp)).format(UTC_TIMESTAMP),
        index: index,
        isLast: index === series[0].data?.length - 1
      });
    },
    [series]
  );

  return (
    <Stack spacing={2}>
      <Typography variant={'subtitle1'}>Daily drilling VFD regenerated energy</Typography>
      {!loading && NoDataAvailable.hasNoData(series) ? (
        <NoDataAvailable height="170px" />
      ) : (
      <CanvasTimeSeriesChart
        highlightPeriod={highlightPeriod}
        syncRefs={syncRefs}
        height={'170px'}
        disableHoverLinePlugin
        enableBarHover
        handlePointClick={handleColumnClick}
        stackedBar
        series={series}
        unit={unit}
        loading={loading}
        chartRef={chartRef}
        includeTooltipSum={true}
        id={'daily-regenerated-energy-chart'}
      />
      )}
    </Stack>
  );
}

DailyRegeneratedEnergyDrawworks.propTypes = {
  chartData: PropTypes.shape({
    unit: PropTypes.string,
    reused: PropTypes.arrayOf(
        PropTypes.shape({
          x: PropTypes.number,
          y: PropTypes.number
        })
      ),
    burned: PropTypes.arrayOf(
      PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number
      })
    ),
    setSelectedColumn: PropTypes.func.isRequired
  }),
  loading: PropTypes.bool,
  syncRefs: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
  ).isRequired,
  chartRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
    .isRequired,
  highlightPeriod: PropTypes.shape({
    startIndex: PropTypes.number,
    endIndex: PropTypes.number
  })
};

DailyRegeneratedEnergyDrawworks.defaultProps = {
  chartData: null,
  highlightPeriod: {},
  loading: false
};

export default DailyRegeneratedEnergyDrawworks;
